import { Button, ChevRightIcon, ModuleHeader, type ModuleHeaderProps, Text } from '@viapeople/components'
import type { ActionButton, EvaluateeData, JumpToItem } from './types'

export const EmployeeInfo = ({
	evaluateeData,
	evaluationHeaderConfig,
	variant = 'default',
	className,
}: {
	evaluateeData: EvaluateeData
	evaluationHeaderConfig?: {
		fields: Array<{
			id: string
			displayLabel: string
			enabled: boolean
			order: number
			type: string
		}>
	}
	variant?: 'default' | 'print'
	className?: string
}) => {
	// If no config provided, use default hardcoded fields
	const employeeInfo = evaluationHeaderConfig?.fields
		? evaluationHeaderConfig.fields
				.filter((field) => field.enabled)
				.sort((a, b) => a.order - b.order)
				.map((field) => {
					let value = ''

					// Get value based on field id from evaluateeData
					if (field.id === 'employee_name') {
						value = evaluateeData ? `${evaluateeData.firstName || ''} ${evaluateeData.lastName || ''}`.trim() : ''
					} else if (field.id === 'directManager' && evaluateeData?.directManager) {
						value =
							`${evaluateeData.directManager.firstName || ''} ${evaluateeData.directManager.lastName || ''}`.trim() ||
							''
					} else if (field.id === 'secondaryManager' && evaluateeData?.secondaryManager) {
						value =
							`${evaluateeData.secondaryManager.firstName || ''} ${evaluateeData.secondaryManager.lastName || ''}`.trim() ||
							''
					} else {
						// For custom fields, try to get from evaluateeData using the field id
						const rawValue = (evaluateeData as Record<string, unknown>)?.[field.id]
						value = Array.isArray(rawValue)
							? rawValue
									.filter((v) => v != null)
									.map(String)
									.join(', ')
							: (rawValue as string) || ''
					}

					return {
						label: field.displayLabel,
						value,
					}
				})
		: [
				{
					label: 'Employee Name',
					value: evaluateeData ? `${evaluateeData.firstName || ''} ${evaluateeData.lastName || ''}`.trim() : '',
				},
				...(evaluateeData?.position
					? [
							{
								label: 'Position',
								value: evaluateeData?.position || '',
							},
						]
					: []),
				...(evaluateeData?.directManager
					? [
							{
								label: 'Primary Supervisor',
								value:
									`${evaluateeData.directManager.firstName || ''} ${evaluateeData.directManager.lastName || ''}`.trim() ||
									'',
							},
						]
					: []),
				...(evaluateeData?.secondaryManager
					? [
							{
								label: 'Contributing Supervisor',
								value:
									`${evaluateeData.secondaryManager.firstName || ''} ${evaluateeData.secondaryManager.lastName || ''}`.trim() ||
									'',
							},
						]
					: []),
				...(evaluateeData?.dateOfHire
					? [
							{
								label: 'Date of Hire',
								value: evaluateeData?.dateOfHire || '',
							},
						]
					: []),
			]

	const spacingClasses = variant === 'print' ? 'w-full p-4 mb-4' : 'm-3 p-3'
	const scrollClass = variant === 'print' ? '' : 'scrollbar-visible'

	return (
		<div
			className={`flex flex-col gap-4 rounded-md bg-blue-50 border border-blue-200 ${scrollClass} ${spacingClasses} ${className ?? ''}`.trim()}>
			{employeeInfo
				.filter((info) => info.value)
				.map((info) => (
					<div key={info.label} className='min-w-0'>
						<Text variant='hint' className='text-neutral-400 mb-1'>
							{info.label}
						</Text>
						<Text variant='xsmall' className='break-words'>
							{info.value}
						</Text>
					</div>
				))}
		</div>
	)
}

export const JumpToComponent = ({
	label,
	icon,
	onClick,
}: {
	label: string
	icon?: React.ReactNode
	onClick: () => void
}) => {
	return (
		<button type='button' className='flex items-start justify-between cursor-pointer w-full' onClick={onClick}>
			<div className='flex items-start gap-1'>
				{icon}
				<Text variant='xsmall' weight='semibold' className='text-left'>
					{label}
				</Text>
			</div>
			<ChevRightIcon className='fill-neutral-400' />
		</button>
	)
}
export const Sidebar = ({
	jumpToItems,
	evaluateeData,
	evaluationHeaderConfig,
}: {
	jumpToItems: JumpToItem[]
	evaluateeData: EvaluateeData
	evaluationHeaderConfig?: {
		fields: Array<{
			id: string
			displayLabel: string
			enabled: boolean
			order: number
			type: string
		}>
	}
}) => {
	return (
		<div className='flex flex-col gap-4 w-70 border-r border-neutral-200 overflow-y-auto pb-44'>
			<EmployeeInfo evaluateeData={evaluateeData} evaluationHeaderConfig={evaluationHeaderConfig} />
			<div className='flex flex-col px-4 gap-2.5'>
				<Text variant='xsmall' className='text-neutral-400'>
					Jump to
				</Text>
				{jumpToItems.map((item) => (
					<JumpToComponent
						key={item.id}
						label={item.label}
						onClick={() => {
							const element = document.getElementById(item.id)
							element?.scrollIntoView({ behavior: 'smooth' })
						}}
					/>
				))}
			</div>
		</div>
	)
}

export const Header = ({
	breadcrumb,
	headerActions,
	onActionClick,
}: {
	breadcrumb: ModuleHeaderProps['breadcrumb']
	headerActions: ActionButton[]
	onActionClick: (action: ActionButton) => void
}) => {
	return (
		<div className='flex justify-between items-end'>
			<div>
				<ModuleHeader breadcrumb={breadcrumb} />
			</div>
			<div className='flex gap-2'>
				{headerActions.map((action) => (
					<Button key={action.key} variant={action.variant} onClick={() => onActionClick(action)}>
						{action.label}
					</Button>
				))}
			</div>
		</div>
	)
}
